<script setup>
import {computed, ref, watch} from 'vue'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import FloatLabel from 'primevue/floatlabel'
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'
import customerApi from '@/api/customerApi.js'


const props = defineProps({
  visible: Boolean,
  customer: Object
})
const emit = defineEmits(['update:visible', 'close', 'save'])

const emptyForm = {
  id: null,
  name: '',
  short_name: '',
  address: '',
  email: '',
  phone: '',
  legal_name: '',
  inn: '',
  kpp: '',
  ogrn: '',
  okpo: '',
  requisites_contact_person: '',
  requisites_contact_position: '',
  requisites_acting_on: '',
  requisites_comments: ''
}

const form = ref({ ...emptyForm })
const errors = ref({ name: '', short_name: '' })
const showInnLookup = ref(false)
const innInput = ref('')
const innLoading = ref(false)
const innError = ref('')


watch(
    [() => props.visible, () => props.customer],
    ([vis, customer]) => {
      if (vis) {
        if (customer) {
          const c = JSON.parse(JSON.stringify(customer))
          form.value = {
            ...emptyForm,
            ...c
          }
        } else {
          form.value = { ...emptyForm }
        }
        errors.value = { name: '', short_name: '' }
        showInnLookup.value = false
        innInput.value = ''
        innError.value = ''
      }
    },
    { immediate: true }
)


function close() {
  emit('update:visible', false)
  emit('close')
}

function submit() {
  // Локальная валидация обязательных полей
  errors.value = { name: '', short_name: '' }
  const payload = { ...form.value }
  Object.keys(payload).forEach(k => {
    if (typeof payload[k] === 'string') payload[k] = payload[k].trim()
  })

  if (!payload.name) {
    errors.value.name = 'Укажите полное наименование'
  }
  if (!payload.short_name) {
    errors.value.short_name = 'Укажите краткое наименование'
  }
  if (errors.value.name || errors.value.short_name) {
    return
  }

  // Отправляем плоский объект под CustomerCreateUpdate
  emit('save', payload)
  // Диалог не закрываем здесь — пусть родитель закроет при успешном сохранении
}


function onUpdateVisible(val) {
  emit('update:visible', val)
  if (!val) emit('close')
}

function onInnInput(e) {
  const digits = String(e?.target?.value ?? '').replace(/\D/g, '').slice(0, 10)
  innInput.value = digits
}

// Заполнение полей по ИНН
async function fillByInn() {
  innError.value = ''
  const inn = (innInput.value || '').trim()
  if (inn.length !== 10) {
    innError.value = 'ИНН должен содержать 10 цифр'
    return
  }
  try {
    innLoading.value = true
    const resp = await customerApi.innLookup(inn)
    const d = resp?.data || {}

    // Маппинг ответа в поля формы
    form.value.inn = d.inn || inn
    form.value.kpp = d.kpp || form.value.kpp
    form.value.ogrn = d.ogrn || form.value.ogrn
    form.value.name = d.full_name || d.name || form.value.name
    form.value.short_name = d.name || form.value.short_name
    form.value.legal_name = d.full_name || form.value.legal_name
    form.value.address = (d.address && d.address.raw) || form.value.address
    // okpo в примере нет — оставляем как есть

    // Скрыть блок после успешного автозаполнения
    showInnLookup.value = false
  } catch (e) {
    innError.value = 'Не удалось получить данные по ИНН'
  } finally {
    innLoading.value = false
  }
}


const dialogHeader = computed(() =>
    props.customer ? 'Редактировать данные клиента' : 'Добавить нового клиента'
)
</script>

<template>
  <Dialog
      :visible="visible"
      @update:visible="onUpdateVisible"
      modal
      :header="dialogHeader"
      :style="{ width: '800px' }"
      :closable="true"
      :position="'top'"
  >
    <form @submit.prevent="submit" class="flex flex-col gap-6">
      <Accordion :value="['0']" multiple>
        <AccordionPanel value="0">
          <AccordionHeader>Основные данные</AccordionHeader>
          <AccordionContent>

            <div class="flex flex-col gap-6">
              <!-- Ссылка/кнопка для автозаполнения по ИНН -->
              <div class="-my-2">
                <Button
                    link
                    size="small"
                    class="inn-link-btn"
                    :label="showInnLookup ? 'Скрыть автозаполнение по ИНН' : 'Заполнить по ИНН'"
                    @click.prevent="showInnLookup = !showInnLookup"
                />
              </div>

              <!-- Раскрывающийся блок ввода ИНН -->
              <div v-if="showInnLookup" class="flex items-end gap-2">
                <FloatLabel variant="on" class="flex-1">
                  <InputText
                      id="inn_lookup"
                      :value="innInput"
                      @input="onInnInput"
                      inputmode="numeric"
                      placeholder="10 цифр"
                      class="w-full"
                      :disabled="innLoading"
                      autocomplete="off"
                  />
                  <label for="inn_lookup">ИНН (10 цифр)</label>
                </FloatLabel>
                <Button
                    label="Заполнить"
                    severity="info"
                    variant="outlined"
                    rounded
                    :loading="innLoading"
                    :disabled="innLoading || (innInput?.length !== 10)"
                    @click="fillByInn"
                />
              </div>
              <small v-if="innError" class="p-error">{{ innError }}</small>

              <FloatLabel variant="on">
                <InputText
                    id="name"
                    v-model="form.name"
                    :class="{'p-invalid': !!errors.name}"
                    class="w-full"
                    autocomplete="off"
                />
                <label for="name">Полное наименование</label>
              </FloatLabel>
              <small v-if="errors.name" class="p-error">{{ errors.name }}</small>

              <FloatLabel variant="on">
                <InputText
                    id="short_name"
                    v-model="form.short_name"
                    :class="{'p-invalid': !!errors.short_name}"
                    class="w-full"
                    autocomplete="off"
                />
                <label for="short_name">Краткое наименование</label>
              </FloatLabel>
              <small v-if="errors.short_name" class="p-error">{{ errors.short_name }}</small>

              <FloatLabel variant="on">
                <InputText id="address" v-model="form.address" class="w-full" autocomplete="off" />
                <label for="address">Адрес</label>
              </FloatLabel>
              <FloatLabel variant="on">
                <InputText id="email" v-model="form.email" type="email" class="w-full" autocomplete="off" />
                <label for="email">Email</label>
              </FloatLabel>
              <FloatLabel variant="on">
                <InputText id="phone" v-model="form.phone" class="w-full" autocomplete="off" placeholder="+7(___)___-__-__" />
                <label for="phone">Телефон</label>
              </FloatLabel>
            </div>
          </AccordionContent>
        </AccordionPanel>

        <AccordionPanel value="1">
          <AccordionHeader>Реквизиты</AccordionHeader>
          <AccordionContent>
            <div class="flex flex-col gap-6">
              <FloatLabel variant="on">
                <InputText id="legal_name" v-model="form.legal_name" class="w-full" autocomplete="off" />
                <label for="legal_name">Юридическое наименование</label>
              </FloatLabel>
              <div class="grid grid-cols-2 gap-6">
                <FloatLabel variant="on">
                  <InputText id="inn" v-model="form.inn" class="w-full" autocomplete="off" />
                  <label for="inn">ИНН</label>
                </FloatLabel>
                <FloatLabel variant="on">
                  <InputText id="kpp" v-model="form.kpp" class="w-full" autocomplete="off" />
                  <label for="kpp">КПП</label>
                </FloatLabel>
                <FloatLabel variant="on">
                  <InputText id="ogrn" v-model="form.ogrn" class="w-full" autocomplete="off" />
                  <label for="ogrn">ОГРН</label>
                </FloatLabel>
                <FloatLabel variant="on">
                  <InputText id="okpo" v-model="form.okpo" class="w-full" autocomplete="off" />
                  <label for="okpo">ОКПО</label>
                </FloatLabel>
              </div>
            </div>
          </AccordionContent>
        </AccordionPanel>

        <AccordionPanel value="2">
          <AccordionHeader>Представитель (реквизиты)</AccordionHeader>
          <AccordionContent>
            <div class="flex flex-col gap-6">
              <FloatLabel variant="on">
                <InputText id="requisites_contact_person" v-model="form.requisites_contact_person" class="w-full" autocomplete="off" />
                <label for="requisites_contact_person">Контактное лицо</label>
              </FloatLabel>
              <FloatLabel variant="on">
                <InputText id="requisites_contact_position" v-model="form.requisites_contact_position" class="w-full" autocomplete="off" />
                <label for="requisites_contact_position">Должность</label>
              </FloatLabel>
              <FloatLabel variant="on">
                <InputText id="requisites_acting_on" v-model="form.requisites_acting_on" class="w-full" autocomplete="off" />
                <label for="requisites_acting_on">Действует на основании</label>
              </FloatLabel>
              <FloatLabel variant="on">
                <InputText id="requisites_comments" v-model="form.requisites_comments" class="w-full" autocomplete="off" />
                <label for="requisites_comments">Комментарий к реквизитам</label>
              </FloatLabel>
            </div>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>

      <div class="flex justify-center gap-4 pt-2">
        <Button :label="props.customer ? 'Сохранить' : 'Добавить'" type="submit" severity="info" rounded />
        <Button label="Отмена" type="button" severity="secondary" rounded  @click="close" />
      </div>
    </form>
  </Dialog>
</template>

<style scoped >
.inn-link-btn {
  padding: 0;
  height: auto;
}


.inn-link-btn :deep(.p-button-label) {
  text-decoration-line: underline;
  text-decoration-style: dashed;
  text-underline-offset: 3px;
}
</style>